import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Globe,
  Layout,
  Smartphone,
  Search,
  TrendingUp,
  Zap,
  BarChart3,
  Shield,
  Palette,
  Code2,
  HelpCircle,
} from 'lucide-react';
import { useState } from 'react';

import SEO from '../components/SEO';

const SystemeLandingpagesPage = () => (
  <div className="pt-20">
    <SEO
      title="Landingpages erstellen & Webentwicklung – SEO-optimiert & conversion-stark"
      description="Professionelle Websites und Landingpages die konvertieren. SEO-optimiert, mobil-first, conversion-stark. Webentwicklung aus Magdeburg & Braunschweig, deutschlandweit von LAIzyones."
      path="/systeme-landingpages"
      keywords="Landingpage erstellen, Website erstellen lassen, Webentwicklung Magdeburg, SEO optimierte Website, conversion optimierte Landingpage, Web-Applikation entwickeln, professionelle Website, Mobile-First Design"
      breadcrumbs={[
        { name: 'Startseite', url: '/' },
        { name: 'Systeme & Landingpages' },
      ]}
      serviceSchema={{
        name: 'Systeme & Landingpages',
        description: 'Professionelle Websites, Landingpages und Web-Applikationen: SEO-optimiert, conversion-stark und mobil-first.',
      }}
      faqSchema={[
        { question: 'Was kostet eine professionelle Landingpage?', answer: 'Eine conversion-optimierte Landingpage startet ab ca. 2.000 Euro. Komplexere Websites und Systeme kosten entsprechend mehr. Im kostenlosen Erstgespräch erstellen wir ein transparentes Angebot.' },
        { question: 'Wie lange dauert die Erstellung einer Website?', answer: 'Eine Landingpage ist in 1–2 Wochen fertig. Unternehmenswebsites benötigen 3–6 Wochen. Sie erhalten vor Projektstart einen verbindlichen Zeitplan.' },
        { question: 'Ist SEO bei der Website-Erstellung inklusive?', answer: 'Ja! Technisches SEO, Meta-Tags, Ladezeit-Optimierung und strukturierte Daten sind bei jeder Website von uns Standard. Auf Wunsch bieten wir auch laufende SEO-Betreuung an.' },
        { question: 'Kann ich die Website selbst aktualisieren?', answer: 'Ja. Wir schulen Sie in der Bedienung Ihres Content-Management-Systems. Alternativ übernehmen wir die Pflege im Rahmen eines Wartungsvertrags.' },
      ]}
    />
    <HeroSection />
    <ServicesSection />
    <WhyItMattersSection />
    <ProcessSection />
    <FAQSection />
    <CTASection />
  </div>
);

const HeroSection = () => (
  <section className="section bg-gray-50 relative overflow-hidden">
    <div className="absolute inset-0 grid-pattern" />
    <div className="container-custom relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="badge mb-6 fade-up">Webentwicklung & Systeme</div>
        <div className="reveal-container">
          <h1 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-gray-900 reveal-text mb-6">
            Systeme & <span className="gradient-text">Landingpages</span> die konvertieren
          </h1>
        </div>
        <p className="body-text text-base md:text-lg text-gray-600 max-w-3xl mx-auto fade-up mb-8">
          Professionelle Websites, Landingpages und Geschäftssysteme, die nicht nur gut aussehen,
          sondern Besucher in Kunden verwandeln — SEO-optimiert, conversion-stark und mobil-first.
          Aus Magdeburg und Braunschweig, für Unternehmen deutschlandweit.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center fade-up">
          <Link to="/#kontakt" className="btn-primary text-base">
            <span>Projekt anfragen</span>
            <ArrowRight className="w-5 h-5 relative z-10" />
          </Link>
          <Link to="/prozessanalyse" className="btn-secondary text-base inline-flex items-center justify-center gap-2">
            Prozessanalyse
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const ServicesSection = () => {
  const services = [
    { icon: Layout, title: 'Landingpages', desc: 'Conversion-optimierte Landingpages, die Besucher zu Kunden machen. A/B-getestet, SEO-optimiert und blitzschnell.' },
    { icon: Globe, title: 'Unternehmenswebsites', desc: 'Professionelle Webauftritte, die Vertrauen schaffen und Ihre Marke stärken — mit klarer Struktur und modernem Design.' },
    { icon: Code2, title: 'Web-Applikationen', desc: 'Maßgeschneiderte Webanwendungen für Ihre Geschäftsprozesse: CRM-Systeme, Kundenportale, Buchungssysteme und mehr.' },
    { icon: Smartphone, title: 'Mobile-First Design', desc: 'Jede Website wird zuerst für mobile Geräte optimiert — für die beste Nutzererfahrung auf jedem Gerät.' },
    { icon: Search, title: 'SEO-Optimierung', desc: 'Suchmaschinenoptimierung von Anfang an: Technisches SEO, Content-Strategie und lokale Sichtbarkeit für Ihr Unternehmen.' },
    { icon: BarChart3, title: 'Conversion-Optimierung', desc: 'Datengetriebene Optimierung Ihrer Website für mehr Anfragen, Leads und Umsatz — messbar und nachvollziehbar.' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unsere Leistungen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Websites & Systeme, die <span className="gradient-text">Ergebnisse liefern</span>
          </h2>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Landingpages erstellen deutschlandweit — von der einfachen Landing Page bis
            zum komplexen Geschäftssystem, immer mit Fokus auf Conversion und Nutzerfreundlichkeit.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card group animate-card">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-xl flex items-center justify-center mb-5 group-hover:from-emerald-200 group-hover:to-cyan-200 transition-colors">
                  <Icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-['Space_Grotesk'] font-semibold text-lg text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const WhyItMattersSection = () => {
  const stats = [
    { value: '75%', label: 'der Nutzer beurteilen Glaubwürdigkeit anhand des Webdesigns' },
    { value: '53%', label: 'verlassen eine Seite, die länger als 3 Sekunden lädt' },
    { value: '70%', label: 'der Kaufentscheidungen beginnen mit einer Google-Suche' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Warum es zählt</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Ihre Website ist Ihr wichtigster <span className="gradient-text">Vertriebsmitarbeiter</span>
          </h2>
          <p className="body-text text-base md:text-lg text-gray-600 fade-up">
            Eine professionelle, schnelle und SEO-optimierte Website ist heute kein Nice-to-have,
            sondern überlebenswichtig für Ihr Unternehmen.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="tech-card p-8 text-center animate-card">
              <span className="block font-['Space_Grotesk'] font-bold text-4xl text-emerald-400 mb-3">{s.value}</span>
              <p className="text-gray-300 text-sm leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const steps = [
    { num: '01', title: 'Anforderungsanalyse', desc: 'Wir verstehen Ihr Geschäft, Ihre Zielgruppe und definieren klare Ziele für Ihre neue Website.' },
    { num: '02', title: 'Design & Konzept', desc: 'Wireframes, UI-Design und Content-Strategie — alles abgestimmt auf Ihre Marke und Conversion-Ziele.' },
    { num: '03', title: 'Entwicklung', desc: 'Moderne, performante Umsetzung mit den neuesten Technologien. SEO-optimiert von Tag 1.' },
    { num: '04', title: 'Launch & Optimierung', desc: 'Go-Live mit Monitoring, Analytics und laufender Conversion-Optimierung für beste Ergebnisse.' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge mb-4 fade-up">Unser Prozess</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            Von der Idee zur <span className="gradient-text">fertigen Website</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="card text-center animate-card relative">
              <span className="font-['Space_Grotesk'] font-bold text-3xl gradient-text mb-3 block">{s.num}</span>
              <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 mb-2">{s.title}</h3>
              <p className="text-gray-600 text-xs leading-relaxed">{s.desc}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 text-emerald-300">
                  <ChevronRight className="w-5 h-5" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: 'Was kostet eine professionelle Landingpage?', a: 'Eine conversion-optimierte Landingpage startet ab ca. 2.000 Euro. Komplexere Websites und Systeme kosten entsprechend mehr. Im kostenlosen Erstgespräch erstellen wir ein transparentes Angebot.' },
    { q: 'Wie lange dauert die Erstellung einer Website?', a: 'Eine Landingpage ist in 1–2 Wochen fertig. Unternehmenswebsites benötigen 3–6 Wochen, komplexe Web-Applikationen entsprechend länger. Sie erhalten vor Projektstart einen verbindlichen Zeitplan.' },
    { q: 'Ist SEO bei der Website-Erstellung inklusive?', a: 'Ja! Technisches SEO, Meta-Tags, Ladezeit-Optimierung und strukturierte Daten sind bei jeder Website von uns Standard. Auf Wunsch bieten wir auch laufende SEO-Betreuung an.' },
    { q: 'Kann ich die Website selbst aktualisieren?', a: 'Ja. Wir schulen Sie in der Bedienung Ihres Content-Management-Systems. Alternativ übernehmen wir die Pflege im Rahmen eines Wartungsvertrags.' },
  ];

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="badge mb-4 fade-up"><HelpCircle className="w-3.5 h-3.5" /> Häufige Fragen</div>
          <h2 className="heading-lg text-3xl md:text-4xl text-gray-900 mb-4 fade-up">
            FAQ zu <span className="gradient-text">Systemen & Landingpages</span>
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="card overflow-hidden">
              <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left group">
                <h3 className="font-['Space_Grotesk'] font-semibold text-base text-gray-900 pr-4 group-hover:text-emerald-600 transition-colors">{faq.q}</h3>
                <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-emerald-500' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === i ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = () => (
  <section className="section bg-white pb-24 md:pb-32">
    <div className="container-custom">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A0F14] to-[#111820] p-8 md:p-12 lg:p-16">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h2 className="font-['Space_Grotesk'] font-bold text-3xl md:text-4xl text-white mb-6">
            Bereit für eine Website, die <span className="text-emerald-400">wirklich verkauft</span>?
          </h2>
          <p className="text-gray-400 text-base md:text-lg mb-8">
            Erzählen Sie uns von Ihrem Projekt. Wir beraten Sie kostenlos und unverbindlich
            zu Design, Technik und Conversion-Strategie.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#kontakt" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold px-8 py-4 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all">
              Kostenlose Beratung <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/geschaeftsideen" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/5 transition-all">
              Start-Up Umsetzung
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default SystemeLandingpagesPage;
